import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEncuesta, votarEncuesta } from '../services/encuestas';
import { getIdentifiers } from '../services/fingerprint';
import { executeRecaptcha } from '../services/recaptcha';
import { validarTiempoRespuesta } from '../utils/validation';
import Loading from '../components/Loading';
import ErrorMessage from '../components/ErrorMessage';
import FormularioEncuesta from '../components/FormularioEncuesta';
import FormularioDemografico from '../components/FormularioDemografico';
import './EncuestaPage.css';

/**
 * Página de encuesta individual
 * Muestra la encuesta completa y permite participar
 */
const EncuestaPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [encuesta, setEncuesta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [identifiers, setIdentifiers] = useState(null);
  const [yaParticipo, setYaParticipo] = useState(false);
  
  // Estados del formulario
  const [respuestas, setRespuestas] = useState({});
  const [esFormularioValido, setEsFormularioValido] = useState(false);
  const [mostrarFormularioDemografico, setMostrarFormularioDemografico] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [tiempoInicio] = useState(Date.now());
  const [votoEnviado, setVotoEnviado] = useState(false); // Prevenir envíos múltiples
  
  const formRef = useRef(null);

  useEffect(() => {
    const loadEncuesta = async () => {
      try {
        setLoading(true);
        setError(null);

        // Generar identificadores
        const ids = await getIdentifiers();
        setIdentifiers(ids);

        // Cargar encuesta con identificadores
        const data = await getEncuesta(
          parseInt(id),
          ids.fingerprint,
          ids.cookieToken
        );

        setEncuesta(data);
        setYaParticipo(data.ya_participo || false);
      } catch (err) {
        console.error('Error al cargar encuesta:', err);
        setError(
          err.response?.data?.detail || 
          'Error al cargar la encuesta. Por favor intenta nuevamente.'
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadEncuesta();
    }
  }, [id]);

  const handleEnviarVoto = async (datosDemograficos) => {
    // Prevenir envíos múltiples
    if (votoEnviado || enviando) {
      console.warn('Intento de envío múltiple bloqueado');
      return;
    }
    
    if (!identifiers || !esFormularioValido) {
      setError('Por favor completa todas las preguntas antes de enviar.');
      setMostrarFormularioDemografico(false);
      return;
    }

    try {
      setEnviando(true);
      setError(null);
      // NO marcar votoEnviado aquí todavía - solo después de validar reCAPTCHA

      // Preparar respuestas en formato correcto
      const respuestasArray = Object.values(respuestas).map(resp => ({
        id_pregunta: resp.id_pregunta,
        id_opcion: resp.id_opcion || null,
        respuesta_texto: resp.respuesta_texto || null,
      }));

      // Calcular tiempo total
      const tiempoTotal = Math.floor((Date.now() - tiempoInicio) / 1000);
      
      // Validar tiempo mínimo razonable
      const numPreguntas = encuesta.preguntas?.length || 0;
      const validacionTiempo = validarTiempoRespuesta(tiempoTotal, numPreguntas);
      if (!validacionTiempo.valido) {
        setError(validacionTiempo.mensaje);
        setMostrarFormularioDemografico(false);
        setEnviando(false);
        return;
      }

      // Ejecutar reCAPTCHA JUSTO ANTES de enviar (para evitar expiración del token)
      let recaptchaToken;
      try {
        console.log('🔄 Ejecutando reCAPTCHA justo antes de enviar...');
        recaptchaToken = await executeRecaptcha('vote');
        if (!recaptchaToken || recaptchaToken.length === 0) {
          throw new Error('No se pudo obtener token de reCAPTCHA o el token está vacío');
        }
        console.log(`✅ Token de reCAPTCHA obtenido (length: ${recaptchaToken.length})`);
        console.log(`Token (primeros 50 chars): ${recaptchaToken.substring(0, 50)}...`);
      } catch (recaptchaError) {
        console.error('❌ Error de reCAPTCHA:', recaptchaError);
        setError('Error de seguridad. Por favor verifica tu conexión e intenta nuevamente. Si el problema persiste, verifica que el dominio esté autorizado en Google reCAPTCHA Admin.');
        setMostrarFormularioDemografico(false);
        setEnviando(false);
        return; // Salir temprano si reCAPTCHA falla
      }

      // Solo ahora marcar como enviado (después de validar reCAPTCHA exitosamente)
      setVotoEnviado(true);

      // Preparar datos del voto (con token fresco)
      const datosVoto = {
        fingerprint_hash: identifiers.fingerprint,
        cookie_token: identifiers.cookieToken,
        recaptcha_token: recaptchaToken,
        respuestas: respuestasArray,
        datos_demograficos: datosDemograficos || null,
        tiempo_total: tiempoTotal,
      };

      console.log('📤 Enviando voto con token de reCAPTCHA...');
      // Enviar voto inmediatamente después de obtener el token
      await votarEncuesta(parseInt(id), datosVoto);

      // Redirigir a página de confirmación
      navigate('/gracias', {
        state: {
          encuestaTitulo: encuesta.encuesta.titulo,
        },
      });
    } catch (err) {
      console.error('Error al enviar voto:', err);
      
      // Manejar diferentes tipos de errores
      let mensajeError = 'Error al registrar tu voto. Por favor intenta nuevamente.';
      
      if (err.response) {
        const status = err.response.status;
        const detail = err.response.data?.detail || '';
        
        if (status === 429) {
          // Rate limiting
          mensajeError = detail || 'Demasiados intentos. Por favor espera unos minutos antes de intentar nuevamente.';
        } else if (status === 400) {
          // Error de validación
          mensajeError = detail || 'Error de validación. Por favor verifica tus respuestas.';
        } else if (status === 403) {
          // Acceso denegado
          mensajeError = detail || 'No tienes permiso para realizar esta acción.';
        } else {
          mensajeError = detail || mensajeError;
        }
      }
      
      setError(mensajeError);
      setMostrarFormularioDemografico(false);
      setVotoEnviado(false); // Permitir reintento si falló
      
      // Scroll al error
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return <Loading message="Cargando encuesta..." fullScreen />;
  }

  if (error) {
    return (
      <div className="encuesta-page">
        <div className="container">
          <ErrorMessage message={error} type="error" />
          <button 
            className="btn-back"
            onClick={() => navigate('/')}
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  if (!encuesta) {
    return (
      <div className="encuesta-page">
        <div className="container">
          <ErrorMessage message="Encuesta no encontrada" type="error" />
          <button 
            className="btn-back"
            onClick={() => navigate('/')}
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="encuesta-page">
      <div className="container">
        {yaParticipo ? (
          <div className="encuesta-already-voted">
            <div className="already-voted-content">
              <h2>✓ Ya participaste en esta encuesta</h2>
              <p>Gracias por tu participación. Solo puedes votar una vez por encuesta.</p>
              <button 
                className="btn-back"
                onClick={() => navigate('/')}
              >
                Ver otras encuestas
              </button>
            </div>
          </div>
        ) : (
          <div className="encuesta-content">
            <header className="encuesta-header">
              <h1>{encuesta.encuesta.titulo}</h1>
              {encuesta.encuesta.descripcion && (
                <p className="encuesta-description">{encuesta.encuesta.descripcion}</p>
              )}
            </header>

            <div className="encuesta-info">
              {encuesta.encuesta.tiempo_estimado && (
                <span>⏱️ {encuesta.encuesta.tiempo_estimado}</span>
              )}
            </div>

            {/* Mostrar error si existe */}
            {error && (
              <ErrorMessage 
                message={error} 
                type="error" 
                dismissible={false}
              />
            )}

            {/* Formulario de encuesta */}
            <div ref={formRef} className="encuesta-form-wrapper">
              <FormularioEncuesta
                preguntas={encuesta.preguntas || []}
                respuestas={respuestas}
                onRespuestaChange={setRespuestas}
                onValidacionChange={(valido, respondidas, total) => {
                  setEsFormularioValido(valido);
                }}
              />
            </div>

            {/* Botón de continuar */}
            <div className="encuesta-actions">
              <button
                className="btn-continuar"
                onClick={() => setMostrarFormularioDemografico(true)}
                disabled={!esFormularioValido || enviando}
              >
                {enviando ? 'Enviando...' : 'Continuar con datos demográficos'}
              </button>
            </div>
          </div>
        )}

        {/* Formulario demográfico modal */}
        {mostrarFormularioDemografico && (
          <FormularioDemografico
            onSubmit={handleEnviarVoto}
            onCancel={() => setMostrarFormularioDemografico(false)}
          />
        )}
      </div>
    </div>
  );
};

export default EncuestaPage;

